<div class="container-fluid">
  <div class="row">
    <div class="col-12">
      <h3>
        <i class="fa fa-credit-card"></i> LawPay Payment Queue
        <button class="btn btn-sm btn-outline-secondary float-right" (click)="refresh()" [disabled]="loading">
          <i class="fa fa-refresh" [class.fa-spin]="loading"></i> Refresh
        </button>
      </h3>
      <p class="text-muted">Payments received from LawPay awaiting processing</p>
    </div>
  </div>

  <div class="row">
    <!-- Payment List -->
    <div class="col-md-6">
      <div class="card">
        <div class="card-header">
          <strong>Unprocessed Payments</strong>
          <span class="badge badge-primary float-right">{{ totalCount }}</span>
        </div>
        <div class="card-body p-0">
          <div *ngIf="loading" class="text-center p-4">
            <i class="fa fa-spinner fa-spin fa-2x"></i>
            <p>Loading payments...</p>
          </div>

          <div *ngIf="!loading && payments.length === 0" class="text-center p-4 text-muted">
            <i class="fa fa-check-circle fa-3x mb-2"></i>
            <p>No unprocessed payments</p>
          </div>

          <table *ngIf="!loading && payments.length > 0" class="table table-hover table-sm mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Payer</th>
                <th class="text-right">Amount</th>
                <th>Payment Method</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let payment of payments"
                  (click)="selectPayment(payment)"
                  [class.table-active]="selectedPayment?.id === payment.id"
                  style="cursor: pointer;">
                <td>{{ formatDate(payment.created_at) }}</td>
                <td>
                  <strong>{{ payment.payer_name || 'Unknown' }}</strong>
                  <br *ngIf="payment.payer_email">
                  <small class="text-muted">{{ payment.payer_email }}</small>
                </td>
                <td class="text-right">
                  <strong>{{ payment.formatted_amount }}</strong>
                </td>
                <td>
                  <span *ngIf="payment.card_brand">
                    {{ payment.card_brand | titlecase }} *{{ payment.card_last_four }}
                  </span>
                  <span *ngIf="!payment.card_brand">
                    {{ payment.payment_method_type | titlecase }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="card-footer" *ngIf="totalPages > 1">
          <pagination
            [totalItems]="totalCount"
            [itemsPerPage]="perPage"
            [(ngModel)]="currentPage"
            (pageChanged)="pageChanged($event)"
            [boundaryLinks]="true"
            [maxSize]="5"
            previousText="&lsaquo;"
            nextText="&rsaquo;"
            firstText="&laquo;"
            lastText="&raquo;">
          </pagination>
        </div>
      </div>
    </div>

    <!-- Selected Payment Details -->
    <div class="col-md-6">
      <div class="card" *ngIf="selectedPayment">
        <div class="card-header bg-primary text-white">
          <strong>Payment Details</strong>
          <button class="btn btn-sm btn-outline-light float-right" (click)="cancelSelection()">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="card-body">
          <!-- Payment Info -->
          <div class="mb-3">
            <h5>{{ selectedPayment.formatted_amount }}</h5>
            <p class="text-muted mb-1">
              {{ selectedPayment.payment_method_display }}
            </p>
            <p class="text-muted mb-1">
              <small>Transaction: {{ selectedPayment.lawpay_transaction_id }}</small>
            </p>
            <p class="text-muted mb-0">
              <small>Received: {{ formatDate(selectedPayment.created_at) }}</small>
            </p>
          </div>

          <hr>

          <!-- Payer Info -->
          <div class="mb-3">
            <h6><i class="fa fa-user"></i> Payer Information</h6>
            <p class="mb-1"><strong>{{ selectedPayment.payer_name || 'Not provided' }}</strong></p>
            <p class="mb-1" *ngIf="selectedPayment.payer_email">{{ selectedPayment.payer_email }}</p>
            <p class="mb-1" *ngIf="selectedPayment.payer_phone">{{ selectedPayment.payer_phone }}</p>
            <p class="mb-0" *ngIf="selectedPayment.invoice_reference">
              <small>Reference: {{ selectedPayment.invoice_reference }}</small>
            </p>
            <p class="mb-0" *ngIf="selectedPayment.description">
              <small>{{ selectedPayment.description }}</small>
            </p>
          </div>

          <hr>

          <!-- Contact Matching -->
          <div class="mb-3">
            <h6>
              <i class="fa fa-address-book"></i> Contact
              <button class="btn btn-sm btn-link float-right p-0" (click)="toggleContactSearch()">
                {{ showContactSearch ? 'Cancel' : 'Change' }}
              </button>
            </h6>

            <div *ngIf="selectedContact" class="alert alert-success py-2 mb-2">
              <strong>{{ selectedContact.name }}</strong>
              <button class="btn btn-sm btn-link text-danger float-right p-0" (click)="clearContact()">
                <i class="fa fa-times"></i>
              </button>
            </div>

            <div *ngIf="!selectedContact && !showContactSearch" class="alert alert-warning py-2 mb-2">
              <i class="fa fa-exclamation-triangle"></i> No contact matched
              <button class="btn btn-sm btn-outline-warning float-right" (click)="toggleContactSearch()">
                Search
              </button>
            </div>

            <div *ngIf="showContactSearch" class="mb-2">
              <div class="input-group input-group-sm">
                <input type="text"
                       class="form-control"
                       placeholder="Search contacts..."
                       [(ngModel)]="contactSearchTerm"
                       (keyup)="searchContacts()">
                <div class="input-group-append">
                  <button class="btn btn-outline-secondary" type="button" (click)="searchContacts()">
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>
              <ul class="list-group mt-1" *ngIf="contactSearchResults.length > 0">
                <li class="list-group-item list-group-item-action py-1"
                    *ngFor="let contact of contactSearchResults"
                    (click)="selectContact(contact)"
                    style="cursor: pointer;">
                  {{ contact.name }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Matter Matching -->
          <div class="mb-3">
            <h6>
              <i class="fa fa-briefcase"></i> Matter
              <button class="btn btn-sm btn-link float-right p-0" (click)="toggleMatterSearch()">
                {{ showMatterSearch ? 'Cancel' : 'Change' }}
              </button>
            </h6>

            <div *ngIf="selectedMatter" class="alert alert-success py-2 mb-2">
              <strong>{{ selectedMatter.number }}</strong> - {{ selectedMatter.name }}
              <button class="btn btn-sm btn-link text-danger float-right p-0" (click)="clearMatter()">
                <i class="fa fa-times"></i>
              </button>
            </div>

            <div *ngIf="!selectedMatter && !showMatterSearch" class="alert alert-warning py-2 mb-2">
              <i class="fa fa-exclamation-triangle"></i> No matter matched
              <button class="btn btn-sm btn-outline-warning float-right" (click)="toggleMatterSearch()">
                Search
              </button>
            </div>

            <div *ngIf="showMatterSearch" class="mb-2">
              <div class="input-group input-group-sm">
                <input type="text"
                       class="form-control"
                       placeholder="Search matters..."
                       [(ngModel)]="matterSearchTerm"
                       (keyup)="searchMatters()">
                <div class="input-group-append">
                  <button class="btn btn-outline-secondary" type="button" (click)="searchMatters()">
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>
              <ul class="list-group mt-1" *ngIf="matterSearchResults.length > 0">
                <li class="list-group-item list-group-item-action py-1"
                    *ngFor="let matter of matterSearchResults"
                    (click)="selectMatter(matter)"
                    style="cursor: pointer;">
                  <strong>{{ matter.number }}</strong> - {{ matter.name }}
                </li>
              </ul>
            </div>
          </div>

          <hr>

          <!-- Action Buttons -->
          <div class="text-center" *ngIf="selectedPayment.processing_status !== 'processed'">
            <button class="btn btn-success btn-lg"
                    (click)="processToReceipt()"
                    [disabled]="processing || !canManualProcess()">
              <i class="fa fa-arrow-right" *ngIf="!processing"></i>
              <i class="fa fa-spinner fa-spin" *ngIf="processing"></i>
              Process Receipt
            </button>
            <p class="text-muted mt-2" *ngIf="!selectedMatter">
              <small>Please select a matter to continue</small>
            </p>
          </div>
          <div class="text-center" *ngIf="selectedPayment.processing_status === 'processed'">
            <button class="btn btn-outline-secondary"
                    (click)="viewEntryGroup(selectedPayment)"
                    *ngIf="selectedPayment.entry_group_id">
              <i class="fa fa-sitemap"></i> View LawPay Entry Group
            </button>
            <p class="text-muted mt-2">
              <small>This LawPay payment has already been processed.</small>
            </p>
          </div>
        </div>
      </div>

      <!-- No selection placeholder -->
      <div class="card" *ngIf="!selectedPayment">
        <div class="card-body text-center text-muted py-5">
          <i class="fa fa-hand-pointer-o fa-3x mb-3"></i>
          <p>Select a payment from the list to view details and process</p>
        </div>
      </div>
    </div>
  </div>
</div>
